import React, { useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import Collapse from '@material-ui/core/Collapse';
import styled from 'styled-components';
import { Box, Button, Spinner } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { borders, spaces, typography } from '@99designs/emu/settings';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { CurrencyCode } from '@99designs/i18n';
import { IconDown1, IconUp1 } from '@99designs/icons';
import { FeaturesList } from '@99designs/spa-common';
import { Desktop, Mobile } from '@99designs/spa-common';
import { trackEvent } from '@99designs/tracking';
import { useOnboarder } from '../../../components/Onboarder/OnboarderContext';
import { Price } from '../../../components/Price';
import { svgBase64ToAscii } from '../../../utils/svgBase64ToAscii';
import { StartProjectBriefProvider } from '../../StartBrief';
import { ONBOARDER_NAME } from '../../eventTracking';
import { useLocalExchangeRate } from '../LocalExchangeRateContext';
import type { Money } from '../../../components/Price';
import type { Product } from '../../Product';
import type { Data } from '../../config';

interface Props {
    product: Product;
}

const CardContainer = styled.div`
    padding: ${spaces['2bu']};
    border: ${borders.content};
    border-radius: ${borders.borderRadiuses.medium}px;
`;

const StyledHeading = styled.div`
    font-weight: ${typography.fontWeights.medium};
    font-size: ${typography.typeScale.size4}px;
    font-family: ${typography.fontFamilies.heading};
    margin-bottom: 0;
    display: inline;

    ${mediaQuery.only('small')} {
        font-size: ${typography.typeScale.size4}px;
    }
`;

const FeaturesListContainer = styled.div`
    ${mediaQuery.minimum('normal')} {
        padding-bottom: ${spaces['1.5bu']};
    }
`;

const FeaturesToggle = styled.div`
    cursor: pointer;
    display: inline;
`;

const Description = styled.p`
    margin: 0;
    padding-right: ${spaces['1.5bu']};

    ${mediaQuery.between('small', 'medium')} {
        padding-right: 0;
        padding-bottom: ${spaces['1.5bu']};
    }
`;

const Logo = styled.div`
    height: 46px;

    ${mediaQuery.only('small')} {
        height: 35px;
    }

    svg {
        display: inline-block;
        width: inherit;
        height: 100%;
    }
`;

const StyledButton = styled(Button)`
    width: 100%;
    margin-top: ${spaces['1.5bu']};
` as typeof Button;

export interface ProductPriceProps {
    fromPrice: Money;
    isFixed?: boolean;
}

function ProductPriceMessage({ fromPrice, isFixed }: ProductPriceProps) {
    if (fromPrice.amountInCents <= 0) {
        return null;
    }

    if (!isFixed) {
        return (
            <FormattedReactMessage
                message={__('From <FromPrice>price_noop</FromPrice>')}
                replacements={{
                    FromPrice: (
                        <Price
                            amountInCents={fromPrice.amountInCents}
                            currency={fromPrice.currency as CurrencyCode}
                        />
                    ),
                }}
            />
        );
    }

    return <Price amountInCents={fromPrice.amountInCents} currency={fromPrice.currency} />;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function StartProjectBriefButton({ product, data }: { product: Product; data: any }) {
    return (
        <StartProjectBriefProvider data={product.getCreateBriefVariables(data)}>
            {({ loading, startBrief }) => (
                <StyledButton
                    size="large"
                    appearance="secondary"
                    width="fill"
                    onClick={() => {
                        startBrief();
                    }}
                    disabled={loading}
                >
                    {loading ? <Spinner color="grey500" size="medium" inline /> : __('Get quote')}
                </StyledButton>
            )}
        </StartProjectBriefProvider>
    );
}

export function SecondaryRecommendationCard({ product }: Props) {
    const { data } = useOnboarder<Data>();
    const exchangeRate = useLocalExchangeRate();
    const localisedPricing = product.getLocalisedPricing(exchangeRate);
    const [open, setOpen] = useState(false);

    return (
        <CardContainer data-testid="recommendation-card">
            <Logo
                dangerouslySetInnerHTML={{
                    __html: svgBase64ToAscii(product.logo || ''),
                }}
            />
            <Mobile>
                <Box paddingTop="1.5bu">
                    <StyledHeading>
                        <ProductPriceMessage
                            fromPrice={localisedPricing.fromPrice}
                            isFixed={localisedPricing.isFixed}
                        />
                    </StyledHeading>
                </Box>
            </Mobile>
            <Box display="flex">
                <Box paddingTop="1.5bu">
                    <Description>{product.description}</Description>
                </Box>
                <Desktop>
                    <Box text-align="right" width="200px">
                        <StyledHeading>
                            <ProductPriceMessage
                                fromPrice={localisedPricing.fromPrice}
                                isFixed={localisedPricing.isFixed}
                            />
                        </StyledHeading>
                        {product.workingMethod === 'project' && (
                            <StartProjectBriefButton product={product} data={data} />
                        )}
                    </Box>
                </Desktop>
            </Box>

            <Collapse in={open} timeout="auto" unmountOnExit>
                <FeaturesListContainer>
                    <FeaturesList
                        features={product.features.map((feature) => {
                            return {
                                isAvailable: true,
                                title: feature.title,
                            };
                        })}
                        aria-hidden={!open}
                    />
                </FeaturesListContainer>
            </Collapse>
            <FeaturesToggle
                onClick={() => {
                    setOpen(!open);
                    trackEvent('Clicked Onboarder Element', {
                        onboarder_name: ONBOARDER_NAME,
                        element_name: `View features for ${product.key}`,
                    });
                }}
                aria-expanded={open}
            >
                <StyledHeading>
                    {!open ? __('View features') : __('Hide features')}
                    <Box paddingLeft="0.25bu" display="inline">
                        {open ? <IconUp1 /> : <IconDown1 />}
                    </Box>
                </StyledHeading>
            </FeaturesToggle>

            {product.workingMethod === 'project' && (
                <Mobile>
                    <StartProjectBriefButton product={product} data={data} />
                </Mobile>
            )}
        </CardContainer>
    );
}
